export * from "../../convex/model/company";
import type { Id } from "../lib/api";
import { knownIdFromInput, knownIdInputResult, knownOptionInputResult, type KnownIdOption } from "./idInput";

export type CompanyOption = KnownIdOption<Id<"companies">>;
export type CompanySelection =
  | {
    readonly kind: "selected";
    readonly companyId: Id<"companies">;
    readonly source: "requested" | "fallback";
  }
  | { readonly kind: "none"; readonly reason: "noCompanies" };

export type CompanySelectionView<Company extends CompanyOption = CompanyOption> =
  | {
    readonly kind: "selected";
    readonly companyId: Company["_id"];
    readonly company: Company;
    readonly source: "requested" | "fallback";
  }
  | {
    readonly kind: "none";
    readonly companyId: null;
    readonly company: null;
    readonly reason: "noCompanies";
  };

export const COMPANY_SELECTION_STORAGE_KEY = "lawstream.companyId" as const;

export type CompanySelectionStorageWrite<CompanyId extends string = string> = {
  readonly kind: "set";
  readonly key: typeof COMPANY_SELECTION_STORAGE_KEY;
  readonly value: CompanyId;
};

export const companyStoredSelectionValue = (storedValue: string | null): string | null => {
  const cleaned = storedValue?.trim() ?? "";
  return cleaned === "" ? null : cleaned;
};

export const companySelectionStorageWrite = <CompanyId extends string>(
  companyId: CompanyId,
): CompanySelectionStorageWrite<CompanyId> => ({
  kind: "set",
  key: COMPANY_SELECTION_STORAGE_KEY,
  value: companyId,
});

export const selectCompany = (
  companies: readonly CompanyOption[],
  requested: string | null,
): CompanySelection => {
  const requestedCompany = knownIdInputResult(companies, requested);
  if (requestedCompany.ok) {
    return { kind: "selected", companyId: requestedCompany.value, source: "requested" };
  }

  const first = companies[0];
  return first === undefined
    ? { kind: "none", reason: "noCompanies" }
    : { kind: "selected", companyId: first._id, source: "fallback" };
};

export const selectCompanyId = (
  companies: readonly CompanyOption[],
  requested: string | null,
): Id<"companies"> | null => {
  const selected = selectCompany(companies, requested);
  return selected.kind === "selected" ? selected.companyId : null;
};

export const companySelectionView = <Company extends CompanyOption>(
  companies: readonly Company[],
  requested: string | null,
): CompanySelectionView<Company> => {
  const requestedCompany = knownOptionInputResult(companies, requested);
  if (requestedCompany.ok) {
    return {
      kind: "selected",
      companyId: requestedCompany.value._id,
      company: requestedCompany.value,
      source: "requested",
    };
  }

  const first = companies[0];
  return first === undefined
    ? { kind: "none", companyId: null, company: null, reason: "noCompanies" }
    : {
      kind: "selected",
      companyId: first._id,
      company: first,
      source: "fallback",
    };
};

export const companyIdFromInput = (
  companies: readonly CompanyOption[],
  value: string,
  fallback: Id<"companies"> | null,
): Id<"companies"> | null =>
  knownIdFromInput(companies, value, fallback);
